import React, { useRef, useState, useEffect, forwardRef } from "react";
import Plot from "react-plotly.js";
import styles from "./Correlation.module.css";

import { CorrelationData } from "./Correlation";
import { ColorScale, Layout, Shape } from "plotly.js";

type Point = { x: any; y: any };

// ContextMenu Component
const ContextMenu = forwardRef<
  HTMLDivElement,
  {
    position: [number, number];
    points?: Point;
    visible: boolean;
    onInspectButton?: (point: Point) => void;
  }
>(({ position, points, visible, onInspectButton: onInspectButton }, ref) => {
  if (!visible || !points) return null;

  return (
    <div
      ref={ref}
      className="card border-black py-1"
      style={{
        width: 175,
        height: 150,
        top: position[1] + 5,
        left: position[0] - 175,
        position: "fixed",
        zIndex: 1000,
      }}
    >
      <div className="text-center fw-semibold">
        {`R_1: ${points.x} , R_2: ${points.y}`}
      </div>
      <hr className="my-2" />
      <button
        className={`d-flex justify-content-between w-100 fw-semibold ${styles.menu_btn}`}
        onClick={() => onInspectButton?.(points)}
      >
        <span>Inspect Pair</span>
        <span className="fw-bold">+</span>
      </button>
    </div>
  );
});

// Main Heatmap Component
const CustomContextHeatmap = forwardRef<
  any,
  {
    data: CorrelationData[];
    selectedFile: string;
    selectedPoint?: Point | null;
    onInspectButton?: (points: Point) => void;
  }
>(({ data, selectedFile, selectedPoint, onInspectButton }, ref) => {
  const selectedData = data.find((d) => d.filename === selectedFile);
  // const customData = data.map((d) => d.z);

  const customData: number[][][] = data[0].z.map((_, j) =>
    data[0].z[0].map((_, k) => data.map((d) => d.z[j][k])),
  );

  // Create staggered labels for x-axis
  const createStaggeredLabels = (labels: any[], axis: "x" | "y") => {
    return labels.map((label, index) => {
      if (index % 2 === 0) {
        return label;
      } else {
        if (axis == "x") {
          return `─── ${label}`;
        } else {
          return `${label} ───`;
        }
      }
    });
  };

  const staggeredXLabels = selectedData?.x
    ? createStaggeredLabels(selectedData.x, "x")
    : [];
  const staggeredYLabels = selectedData?.y
    ? createStaggeredLabels(selectedData.y, "y")
    : [];

  const ctxRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<[number, number]>([-999, -999]);
  const [points, setPoints] = useState<Point | undefined>();

  const [layout, setLayout] = useState<Partial<Layout>>({
    title: selectedData?.filename,
    xaxis: {
      constrain: "domain",
      tickangle: 90,
      tickmode: "array",
      tickfont: { size: 8 },
      ticktext: staggeredXLabels,
      tickvals: selectedData?.x,
      ticklen: 5,
      tickwidth: 1,
      // @ts-ignore - Plotly.js supports these properties for label staggering
      tickposition: "outside",
    },
    yaxis: {
      scaleanchor: "x",
      tickangle: 0,
      tickmode: "array",
      tickfont: { size: 8 },
      ticktext: staggeredYLabels,
      tickvals: selectedData?.y,
      ticklen: 5,
      tickwidth: 1,
      // @ts-ignore - Plotly.js supports these properties for label staggering
      tickposition: "outside",
    },
    autosize: true,
    margin: { t: 40, b: 40, l: 0, r: 0 },
  });

  const customColorScale = [
    [0.0, "#004275"],
    [0.01, "#00477b"],
    [0.02, "#014c81"],
    [0.03, "#005288"],
    [0.04, "#02578e"],
    [0.05, "#045c93"],
    [0.06, "#00619a"],
    [0.07, "#0066a0"],
    [0.08, "#006ba5"],
    [0.09, "#046faa"],
    [0.1, "#0474b0"],
    [0.11, "#0b79b5"],
    [0.12, "#097eba"],
    [0.13, "#0d82be"],
    [0.14, "#1587c3"],
    [0.15, "#188bc6"],
    [0.16, "#2090ca"],
    [0.17, "#2694cc"],
    [0.18, "#2d98cf"],
    [0.19, "#349cd0"],
    [0.2, "#3ca0d1"],
    [0.21, "#45a4d2"],
    [0.22, "#4da7d2"],
    [0.23, "#56abd2"],
    [0.24, "#5dafd1"],
    [0.25, "#65b2d0"],
    [0.26, "#6db5d0"],
    [0.27, "#75b8ce"],
    [0.28, "#7bbbcd"],
    [0.29, "#82becc"],
    [0.3, "#87c1cd"],
    [0.31, "#8cc4cd"],
    [0.32, "#91c6cd"],
    [0.33, "#97c9cb"],
    [0.34, "#9bcccb"],
    [0.35, "#9fcecb"],
    [0.36, "#a3d1ca"],
    [0.37, "#a6d3ca"],
    [0.38, "#a8d4ca"],
    [0.39, "#a9d5ca"],
    [0.4, "#abd7ca"],
    [0.41, "#add8ca"],
    [0.42, "#aed9ca"],
    [0.43, "#b0dbca"],
    [0.44, "#b2dcc9"],
    [0.45, "#b3ddc9"],
    [0.46, "#b5dfc9"],
    [0.47, "#b7e0c9"],
    [0.48, "#b8e2c8"],
    [0.49, "#bae3c8"],
    [0.5, "#bde5c9"],
    [0.51, "#bce3c5"],
    [0.52, "#bde2c3"],
    [0.53, "#bee0c1"],
    [0.54, "#bfdfbf"],
    [0.55, "#c0debc"],
    [0.56, "#c0dcba"],
    [0.57, "#c1dbb8"],
    [0.58, "#c2d9b5"],
    [0.59, "#c3d8b3"],
    [0.6, "#c3d7b0"],
    [0.61, "#c4d5ae"],
    [0.62, "#c5d4ab"],
    [0.63, "#c6d3a8"],
    [0.64, "#c7d1a6"],
    [0.65, "#c8d0a3"],
    [0.66, "#c8cea0"],
    [0.67, "#c9cc9b"],
    [0.68, "#cbc995"],
    [0.69, "#cdc690"],
    [0.7, "#cfc389"],
    [0.71, "#d2c083"],
    [0.72, "#d4bc7d"],
    [0.73, "#d6b877"],
    [0.74, "#d8b570"],
    [0.75, "#dab16b"],
    [0.76, "#dcad64"],
    [0.77, "#dea95d"],
    [0.78, "#e1a455"],
    [0.79, "#e2a050"],
    [0.8, "#e49c4b"],
    [0.81, "#e59745"],
    [0.82, "#e79240"],
    [0.83, "#e78e3c"],
    [0.84, "#e88a38"],
    [0.85, "#e98434"],
    [0.86, "#ea8031"],
    [0.87, "#eb7a2d"],
    [0.88, "#eb7529"],
    [0.89, "#ec6f26"],
    [0.9, "#ec6a24"],
    [0.91, "#ed6421"],
    [0.92, "#ed5e1e"],
    [0.93, "#ef551c"],
    [0.94, "#ed501a"],
    [0.95, "#ec4b18"],
    [0.96, "#ec4216"],
    [0.97, "#ed3813"],
    [0.98, "#ed1e0e"],
    [0.99, "#ec2f11"],
    [1.0, "#ec0c0b"],
  ];

  useEffect(() => {
    if (selectedData?.x && selectedData?.y && selectedPoint) {
      const xIndex = selectedData.x.findIndex((x) => x === selectedPoint?.x);
      const yIndex = selectedData.y.findIndex((y) => y === selectedPoint?.y);

      // Only add the bounding box if the point is found in both axes
      if (xIndex !== -1 && yIndex !== -1) {

        const shapes: Partial<Shape>[] = [
          {
            type: "rect",
            xref: "x",
            yref: "y",
            // Use the actual categorical values with scaled offsets
            x0: xIndex - 1.15,
            y0: yIndex - 1.15,
            x1: xIndex + 1.15,
            y1: yIndex + 1.15,
            line: {
              color: "green",
              width: 12.5, // Thinner line for better scaling
            },
            fillcolor: "rgba(0, 255, 0, 0.1)",
          },
        ];

        setLayout((prev) => ({
          ...prev,
          title: selectedData?.filename,
          shapes: shapes,
        }));
      } else {
        setLayout((prev) => ({
          ...prev,
          title: selectedData?.filename,
          shapes: [],
        }));
      }
    } else {
      setLayout((prev) => ({
        ...prev,
        title: selectedData?.filename,
        shapes: [],
      }));
    }
  }, [data, selectedData, selectedPoint]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ctxRef.current && !ctxRef.current.contains(event.target as Node)) {
        setVisible(false);
      }
    };

    const handleBlur = () => {
      setVisible(false);
    };

    if (visible) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("blur", handleBlur);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("blur", handleBlur);
    };
  }, [visible]);

  const handleRelayout = (updatedLayout: any) => {
    setLayout((prev) => ({ ...prev, ...updatedLayout }));
  };

  const handleContextClick = (e: {
    event: MouseEvent;
    points: Array<Point>;
  }) => {
    if (e.event.button !== 2) return;

    e.event.preventDefault();
    const { clientX, clientY } = e.event;
    setPosition([clientX, clientY]);

    if (e.points?.length > 0) {
      const { x, y } = e.points[0];
      setPoints({ x, y });
      setVisible(true);
    }
  };

  const handleInspectButton = (points: Point) => {
    console.log(points);
    if (onInspectButton && points) {
      onInspectButton(points);
    }
    setVisible(false);
  };

  return (
    <div
      className="h-100 py-4"
      onContextMenu={(e) => e.preventDefault()}
      style={{ aspectRatio: 1.25, maxWidth: "100%" }}
    >
      <Plot
        ref={ref}
        className="w-100 h-100"
        data={[
          {
            z: selectedData?.z ?? [],
            x: selectedData?.x ?? [],
            y: selectedData?.y ?? [],
            // @ts-ignore, plotly doesn't accept Datum[][][] as a valid input, but it does work
            customdata: customData,
            type: "heatmap",
            hovertemplate: `X: %{x}   Y: %{y}<br>Correl: %{z:.2f}<br><br>${data
              .filter((d) => d.filename !== selectedFile)
              .map((d, index) => `${d.days} day: %{customdata[${index}]:.2f}`)
              .join("<br>")}<br><extra></extra>`,
            colorbar: {
              xpad: 0,
            },
            colorscale: customColorScale as ColorScale,
            zmin: -1,
            zmax: 1,
            showscale: true,
          },
        ]}
        layout={layout}
        config={{
          responsive: true,
          // scrollZoom: true
        }}
        onClick={handleContextClick}
        onRelayout={handleRelayout}
      />
      <ContextMenu
        position={position}
        points={points}
        ref={ctxRef}
        visible={visible}
        onInspectButton={handleInspectButton}
      />
    </div>
  );
});

export default CustomContextHeatmap;
